/**
 * 海外首页 SSR 数据保护层。
 *
 * 主要职责：
 * 1. 并行获取首页活动与导航菜单，仅限制非核心活动请求阻塞 SSR 的时间；
 * 2. 按接口实际公共参数隔离缓存，避免地区、语言或 gid 不同的响应互相污染；
 * 3. 合并相同缓存键的并发请求，并通过 stale-while-revalidate 降低接口波动对 TTFB 的影响；
 * 4. 校验接口数据结构，在超时或异常时返回不会阻断首页渲染的安全数据。
 *
 * 缓存仅存在于当前 Node.js 进程内，不跨实例共享，也不会持久化。活动请求最多占用 800ms
 * SSR 等待预算，超时后仍允许上游请求在后台填充缓存；菜单属于首屏核心内容，不使用该降级预算，
 * 冷启动时 SSR 会等待菜单请求完成，确保返回的 HTML 包含完整导航。
 */
import type { ServerRequestCommonOptions } from './serverRquest';
import { fetchActivityList, fetchOverseaMenus } from './serverRquest';
import { getServerCommonParams } from './serverFetch';

const ACTIVITY_CACHE_FRESH_MS = 60 * 1000;
const ACTIVITY_CACHE_MAX_STALE_MS = 5 * 60 * 1000;
const MENUS_CACHE_FRESH_MS = 10 * 60 * 1000;
const MENUS_CACHE_MAX_STALE_MS = 60 * 60 * 1000;
const ACTIVITY_SSR_WAIT_TIMEOUT_MS = 800;
const OVERSEA_HOME_REQUEST_TIMEOUT_MS = 3000;
const MAX_CACHE_ENTRIES = 100;
const SSR_WAIT_TIMEOUT_ERROR = new Error('Oversea home SSR wait timeout');

type ActivityData = Awaited<ReturnType<typeof fetchActivityList>>;
type MenusData = Awaited<ReturnType<typeof fetchOverseaMenus>>;

interface CacheEntry<T> {
  data: T;
  updatedAt: number;
}

interface CachePolicy {
  freshMs: number;
  maxStaleMs: number;
}

interface CachedRequestOptions<T> {
  cache: Map<string, CacheEntry<T>>;
  inFlightRequests: Map<string, Promise<T>>;
  key: string;
  load: () => Promise<T>;
  fallback: () => T;
  policy: CachePolicy;
  label: string;
  /** 未配置时等待上游请求完成，用于菜单等不可按时间预算降级的核心数据。 */
  waitTimeoutMs?: number;
}

export interface OverseaHomeServerData {
  activityData: ActivityData | null;
  menus: MenusData;
}

const activityCache = new Map<string, CacheEntry<ActivityData | null>>();
const activityRequests = new Map<string, Promise<ActivityData | null>>();
const menusCache = new Map<string, CacheEntry<MenusData>>();
const menusRequests = new Map<string, Promise<MenusData>>();

/** 控制内存缓存条目数量，避免按地区、语言和 gid 拆分后持续增长。 */
function setBoundedCacheEntry<T>(cache: Map<string, CacheEntry<T>>, key: string, data: T): void {
  // 刷新已有条目时移动到 Map 末尾，避免后续淘汰刚更新的数据。
  if (cache.has(key)) {
    cache.delete(key);
  } else if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value as string | undefined;
    if (oldestKey) cache.delete(oldestKey);
  }
  cache.set(key, { data, updatedAt: Date.now() });
}

/** 合并相同缓存键的并发刷新，避免高并发 SSR 重复请求同一份首页数据。 */
function refreshCacheEntry<T>(
  cache: Map<string, CacheEntry<T>>,
  inFlightRequests: Map<string, Promise<T>>,
  key: string,
  load: () => Promise<T>,
): Promise<T> {
  const existingRequest = inFlightRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = load()
    .then((data) => {
      setBoundedCacheEntry(cache, key, data);
      return data;
    })
    .finally(() => {
      inFlightRequests.delete(key);
    });
  inFlightRequests.set(key, request);
  return request;
}

/** 等待缓存刷新到达 SSR 时间预算；超时只结束本次等待，不中止仍可预热缓存的上游请求。 */
function waitForRequestWithin<T>(request: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      settled = true;
      reject(SSR_WAIT_TIMEOUT_ERROR);
    }, timeoutMs);

    request.then(
      (data) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(data);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

/** 优先返回新鲜缓存，过期后返回旧数据并刷新；冷启动是否受等待预算限制由调用方按数据重要性决定。 */
async function getCachedRequest<T>({
  cache,
  inFlightRequests,
  key,
  load,
  fallback,
  policy,
  label,
  waitTimeoutMs,
}: CachedRequestOptions<T>): Promise<T> {
  const cached = cache.get(key);
  const cacheAge = cached ? Date.now() - cached.updatedAt : Number.POSITIVE_INFINITY;

  if (cached && cacheAge < policy.freshMs) return cached.data;

  const refreshRequest = refreshCacheEntry(cache, inFlightRequests, key, load);
  if (cached && cacheAge < policy.maxStaleMs) {
    refreshRequest.catch((error: unknown) => {
      console.warn(`[Oversea Home] ${label} background refresh failed`, error);
    });
    return cached.data;
  }

  try {
    if (waitTimeoutMs === undefined) return await refreshRequest;
    return await waitForRequestWithin(refreshRequest, waitTimeoutMs);
  } catch (error) {
    if (error === SSR_WAIT_TIMEOUT_ERROR) {
      // 超出 SSR 预算属于预期降级；仅在后台请求最终失败时记录，避免慢请求成功也产生告警噪音。
      refreshRequest.catch((refreshError: unknown) => {
        console.warn(`[Oversea Home] ${label} background warm-up failed`, refreshError);
      });
      return fallback();
    }
    console.warn(`[Oversea Home] ${label} request degraded`, error);
    return fallback();
  }
}

/** 使用服务端实际发送的地区、语言和 gid 参数生成缓存键，避免不同响应互相污染。 */
function getOverseaHomeCacheKey(options: ServerRequestCommonOptions): string {
  const commonParams = getServerCommonParams(options);
  return Object.entries(commonParams)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

/** 请求并校验活动数据，避免异常成功响应被写入缓存。 */
async function loadActivityData(options: ServerRequestCommonOptions): Promise<ActivityData> {
  const data = await fetchActivityList('official_home_top', options);
  if (!data || !Array.isArray(data.list)) {
    throw new Error('Invalid oversea home activity response');
  }
  return data;
}

/** 请求并校验菜单数据，确保页面可以安全读取 menus.list。 */
async function loadMenusData(options: ServerRequestCommonOptions): Promise<MenusData> {
  const data = await fetchOverseaMenus(options);
  if (!data || !Array.isArray(data.list)) {
    throw new Error('Invalid oversea home menus response');
  }
  return data;
}

/** 获取海外首页 SSR 数据：菜单等待真实结果，活动使用短等待预算，二者均复用 SWR 缓存。 */
export async function getOverseaHomeServerData(
  options: ServerRequestCommonOptions,
): Promise<OverseaHomeServerData> {
  const key = getOverseaHomeCacheKey(options);
  const requestOptions = { ...options, timeout: OVERSEA_HOME_REQUEST_TIMEOUT_MS };

  const [activityData, menus] = await Promise.all([
    getCachedRequest({
      cache: activityCache,
      inFlightRequests: activityRequests,
      key,
      load: () => loadActivityData(requestOptions),
      fallback: () => null,
      policy: {
        freshMs: ACTIVITY_CACHE_FRESH_MS,
        maxStaleMs: ACTIVITY_CACHE_MAX_STALE_MS,
      },
      label: 'activity',
      waitTimeoutMs: ACTIVITY_SSR_WAIT_TIMEOUT_MS,
    }),
    getCachedRequest({
      cache: menusCache,
      inFlightRequests: menusRequests,
      key,
      load: () => loadMenusData(requestOptions),
      fallback: () => ({ list: [] }),
      policy: {
        freshMs: MENUS_CACHE_FRESH_MS,
        maxStaleMs: MENUS_CACHE_MAX_STALE_MS,
      },
      label: 'menus',
    }),
  ]);

  return { activityData, menus };
}
